/**
 * Universal file viewer component supporting images, videos, video embeds (YouTube/Vimeo),
 * audio, PDFs, text/code files, documents, archives, and more.
 *
 * @example
 * ```tsx
 * <UniversalFileViewer
 *   opened={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   files={fileArray}
 *   initialIndex={0}
 * />
 * ```
 */

import { forwardRef, useCallback } from 'react';
import { ActionIcon, Tooltip, Box } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Modal } from '@/components/ui';
import { useFileViewer } from '../../hooks/useFileViewer';
import { ViewerToolbar } from './ViewerToolbar';
import { ThumbnailStrip } from './ThumbnailStrip';
import {
  ImageRenderer,
  VideoRenderer,
  VideoEmbedRenderer,
  AudioRenderer,
  PdfRenderer,
  TextRenderer,
  DocumentRenderer,
  ArchiveRenderer,
  FallbackRenderer,
} from './renderers';
import type { UniversalFileViewerProps } from '../../types';

export const UniversalFileViewer = forwardRef<HTMLDivElement, UniversalFileViewerProps>(
  (
    {
      files,
      initialIndex = 0,
      opened,
      onClose,
      onDownload,
      onIndexChange,
      showGallery,
      showToolbar = true,
      enableKeyboard = true,
      className,
    },
    ref
  ) => {
    const viewer = useFileViewer({
      files,
      initialIndex,
      enableKeyboard,
      opened,
      onClose,
      onIndexChange,
    });

    const { currentFile, category } = viewer;
    const showGalleryStrip = showGallery ?? files.length > 1;

    const handleDownload = useCallback(() => {
      if (onDownload) {
        onDownload(currentFile);
        return;
      }
      const url = currentFile.url || currentFile.externalUrl;
      if (!url) return;

      // Use fetch + blob + anchor to force a real download
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error('Download failed');
          return res.blob();
        })
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = currentFile.fileName || 'download';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {
          // Fallback: open in new tab if fetch fails (e.g. CORS)
          window.open(url, '_blank');
        });
    }, [onDownload, currentFile]);

    const handlePrint = useCallback(() => {
      if (currentFile.url) {
        const printWindow = window.open(currentFile.url, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => printWindow.print());
        }
      }
    }, [currentFile]);

    const renderContent = () => {
      if (!currentFile) return null;

      switch (category) {
        case 'image':
          return (
            <ImageRenderer
              src={currentFile.url || ''}
              alt={currentFile.fileName}
              zoom={viewer.zoom}
              rotation={viewer.rotation}
            />
          );

        case 'video':
          return (
            <VideoRenderer
              src={currentFile.url || ''}
              mimeType={currentFile.mimeType}
            />
          );

        case 'video-embed':
          return (
            <VideoEmbedRenderer
              externalUrl={currentFile.externalUrl || ''}
              title={currentFile.fileName}
            />
          );

        case 'audio':
          return (
            <AudioRenderer
              src={currentFile.url || ''}
              fileName={currentFile.fileName}
              mimeType={currentFile.mimeType}
            />
          );

        case 'pdf':
          return (
            <PdfRenderer
              src={currentFile.url || ''}
              title={currentFile.fileName}
            />
          );

        case 'text':
          return (
            <TextRenderer
              src={currentFile.url}
              fileName={currentFile.fileName}
              textContent={currentFile.textContent}
            />
          );

        case 'document':
          return (
            <DocumentRenderer
              file={currentFile}
              onDownload={handleDownload}
            />
          );

        case 'archive':
          return (
            <ArchiveRenderer
              file={currentFile}
              onDownload={handleDownload}
            />
          );

        default:
          return (
            <FallbackRenderer
              file={currentFile}
              onDownload={handleDownload}
            />
          );
      }
    };

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        size="full"
        variant="minimal"
        backdropVariant="dark"
        withCloseButton={false}
        padding={0}
        styles={{
          content: {
            backgroundColor: 'rgba(0, 0, 0, 0.92)',
            height: '100vh',
          },
          body: {
            height: '100%',
            padding: 0,
          },
        }}
      >
        <Box
          ref={ref}
          className={className}
          style={{
            position: 'relative',
            width: '100%',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Toolbar */}
          {showToolbar && (
            <ViewerToolbar
              fileName={currentFile?.fileName || ''}
              fileIndex={viewer.currentIndex}
              totalFiles={files.length}
              zoom={viewer.zoom}
              canZoom={viewer.canZoom}
              canRotate={viewer.canRotate}
              onZoomIn={viewer.zoomIn}
              onZoomOut={viewer.zoomOut}
              onResetZoom={viewer.resetZoom}
              onRotate={viewer.rotate}
              onDownload={currentFile?.url || currentFile?.externalUrl ? handleDownload : undefined}
              onPrint={currentFile?.url ? handlePrint : undefined}
              onClose={onClose}
            />
          )}

          {/* Content area */}
          <Box
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: showToolbar ? 48 : 0,
              paddingBottom: showGalleryStrip ? 76 : 0,
            }}
          >
            {renderContent()}
          </Box>

          {/* Navigation arrows */}
          {files.length > 1 && (
            <>
              {viewer.hasPrev && (
                <Tooltip label="Previous" position="right">
                  <ActionIcon
                    variant="subtle"
                    size="xl"
                    onClick={viewer.goPrev}
                    style={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '50%',
                    }}
                  >
                    <IconChevronLeft size={28} />
                  </ActionIcon>
                </Tooltip>
              )}
              {viewer.hasNext && (
                <Tooltip label="Next" position="left">
                  <ActionIcon
                    variant="subtle"
                    size="xl"
                    onClick={viewer.goNext}
                    style={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 10,
                      color: 'white',
                      backgroundColor: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '50%',
                    }}
                  >
                    <IconChevronRight size={28} />
                  </ActionIcon>
                </Tooltip>
              )}
            </>
          )}

          {/* Thumbnail gallery */}
          {showGalleryStrip && (
            <ThumbnailStrip
              files={files}
              currentIndex={viewer.currentIndex}
              onSelect={viewer.goToIndex}
            />
          )}
        </Box>
      </Modal>
    );
  }
);

UniversalFileViewer.displayName = 'UniversalFileViewer';

export default UniversalFileViewer;
